// Pallet check (2026-10-23): a beacon's QR on a sales-order pallet opens /pallet-check/<pallet_id>, where
// anyone on the floor checks the pallet in front of them against what JobQ expects. API contract:
// jobq-api docs/pallet-check-contract.md. Reads, the two check writes, the manager's Clear, the part photo,
// and the pure rules the screen draws from (banner tone, the problem payload, the stack's layers).
//
// Every call goes through the app's fetch wrapper (config.js adds X-API-Key).

import config from '../../config.js';
import { apiErrorMessage } from '../../utils/apiError';
import { cellHeight, stackLayout } from '../../utils/stackLayout';
import { shopClock } from '../../utils/shopDate';
import { holdingPalletText, isTaskWipPallet } from '../salesOrders/palletRules';

const API_BASE = config.API_BASE_URL;

/** The screen's address for a pallet (utils/scanCodes, where a scanned plt- code goes). */
export { palletCheckPath } from '../../utils/scanCodes';

async function call(path, opts = {}, fallback = 'JobQ could not do that') {
  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, opts);
  } catch (e) {
    return { ok: false, status: 0, body: null, message: 'Could not reach JobQ. Check the connection and try again.' };
  }
  if (!res.ok) {
    // A 4xx detail is already the sentence ("PIN not recognised.", "Pallet not found."): shown as it is.
    const message = await apiErrorMessage(res, res.status >= 500 ? fallback : '');
    return { ok: false, status: res.status, body: null, message };
  }
  let body = null;
  try { body = await res.json(); } catch (_) { body = null; }
  return { ok: true, status: res.status, body, message: null };
}

const jsonPost = (body) => ({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

/** GET /packaging/pallets/{id}/check: {ok, status, body, message}. Never throws. */
export function loadPalletCheck(palletId) {
  return call(`/packaging/pallets/${encodeURIComponent(palletId)}/check`, {}, "Couldn't load this pallet");
}

/** POST /packaging/pallets/{id}/checks with a payload from okPayload / problemPayload. */
export function recordPalletCheck(palletId, payload) {
  return call(`/packaging/pallets/${encodeURIComponent(palletId)}/checks`, jsonPost(payload), "Couldn't record the check");
}

/** POST /packaging/pallets/{id}/checks/{check_id}/clear {pin, note}: a manager clears a problem. */
export function clearPalletProblem(palletId, checkId, { pin, note }) {
  return call(`/packaging/pallets/${encodeURIComponent(palletId)}/checks/${encodeURIComponent(checkId)}/clear`,
    jsonPost({ pin: String(pin || '').trim(), note: String(note || '').trim() }), "Couldn't clear the problem");
}

/**
 * GET /packaging/plans/{plan_id}/pallet_checks for the SO page: {pallet_id: {last_check, open_problems}}.
 * Resolves {} on ANY failure (the endpoint may not be on prod yet) and on an unexpected shape.
 */
export async function loadPlanPalletChecks(planId) {
  if (!planId) return {};
  try {
    const res = await fetch(`${API_BASE}/packaging/plans/${encodeURIComponent(planId)}/pallet_checks`);
    if (!res || !res.ok) return {};
    const body = await res.json();
    const pallets = body && typeof body === 'object' && body.pallets && typeof body.pallets === 'object' ? body.pallets : {};
    return pallets;
  } catch (e) {
    return {};
  }
}

/** The open problems JobQ lists for one pallet in loadPlanPalletChecks' answer. */
export function openProblemsFor(checks, palletId) {
  const row = checks && checks[String(palletId)];
  return row && Array.isArray(row.open_problems) ? row.open_problems : [];
}

// ── The banner (tones from the approved States design) ────────────────────────────────────────────

const TONES = {
  in_progress: { background: '#4B5058', color: '#FFFFFF' },
  stage: { background: '#6D28D9', color: '#FFFFFF' },
  to_vendor: { background: '#F59E0B', color: '#1F1300' },
  at_vendor: { background: '#FFFFFF', color: '#15171A', titleColor: '#7C2D12', border: '3px solid #F59E0B' },
  ready_to_seal: { background: '#15803D', color: '#FFFFFF' },
  all_sealed: { background: '#1D4ED8', color: '#FFFFFF' },
  ready_to_ship: { background: '#1D4ED8', color: '#FFFFFF' },
  staged: { background: '#1D4ED8', color: '#FFFFFF' },
  shipped: { background: '#B91C1C', color: '#FFFFFF' },
  consolidated: { background: '#15171A', color: '#FFFFFF' },
};

/** {background, color, titleColor, border, tagBackground, tagColor} for a banner.state. */
export function bannerTone(state) {
  const t = TONES[state] || TONES.in_progress;
  const dark = t.color === '#1F1300' || t.background === '#FFFFFF';
  return {
    background: t.background,
    color: t.color,
    titleColor: t.titleColor || t.color,
    border: t.border || 'none',
    tagBackground: dark ? '#1F1300' : 'rgba(255,255,255,0.18)',
    tagColor: dark ? '#FCD34D' : '#FFFFFF',
  };
}

// ── The check rows ────────────────────────────────────────────────────────────────────────────────

/** "Kim Smith, 2:40 PM" (shop clock; the date when not today). */
export function whoWhen(check, now = new Date()) {
  if (!check) return '';
  const when = shopClock(check.created_at, now);
  return [check.checked_by, when].filter(Boolean).join(', ');
}

/** The line under the two buttons. */
export function lastCheckLine(check, now = new Date()) {
  if (!check) return 'Last check: none yet.';
  const what = check.outcome === 'ok' ? 'matched' : `problem (${check.problem_label || check.problem_kind || 'reported'})`;
  return `Last check: ${what}, ${whoWhen(check, now)}.`;
}

/** The sentence after "Pallet matches" went through: "Checked by Kim Smith at 2:14 PM." */
export function checkedSentence(check, now = new Date()) {
  if (!check) return 'Check recorded.';
  const when = shopClock(check.created_at, now);
  return `Checked by ${check.checked_by || 'you'}${when ? ` at ${when}` : ''}.`;
}

// ── "Something's wrong" ───────────────────────────────────────────────────────────────────────────

export const PROBLEM_KINDS = Object.freeze([
  { kind: 'wrong_part', title: 'Wrong part', sub: 'A piece doesn’t look like the picture' },
  { kind: 'count_off', title: 'Count is off', sub: 'More or fewer pieces than JobQ says' },
  { kind: 'packed_differently', title: 'Packed differently', sub: 'Layers, boxes or stacks don’t match the picture' },
  { kind: 'label_mismatch', title: 'Label or beacon doesn’t match', sub: 'Another pallet number or order' },
  { kind: 'damaged', title: 'Damaged', sub: 'Bent, scratched or the pallet is broken' },
  { kind: 'other', title: 'Something else', sub: 'Say what in the note' },
]);

export const NOTE_MAX = 1000;
export const PHOTO_MAX_BYTES = 4 * 1024 * 1024;

/** A data: URL (or bare base64) as the API wants it: no "data:...;base64," prefix. */
export function stripDataPrefix(b64) {
  if (!b64) return null;
  const s = String(b64);
  const i = s.indexOf('base64,');
  return s.startsWith('data:') && i >= 0 ? s.slice(i + 'base64,'.length) : s;
}

/** Bytes a base64 string decodes to. */
export function base64Bytes(b64) {
  const s = String(b64 || '').replace(/=+$/, '');
  return Math.floor((s.length * 3) / 4);
}

/**
 * A client_request_id for POST .../checks: made once per submit and sent again on a retry, so a check the
 * API recorded before a timeout isn't recorded twice (it answers `duplicate: true`). <= 64 of [A-Za-z0-9_-].
 */
export function makeRequestId() {
  let id = '';
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') id = crypto.randomUUID();
  } catch (_) { id = ''; }
  if (!id) id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  return id.replace(/[^A-Za-z0-9_-]/g, '').slice(0, 64);
}

/** The "Pallet matches" payload. */
export const okPayload = (pin, requestId = null) => {
  const p = { pin: String(pin || '').trim(), outcome: 'ok' };
  if (requestId) p.client_request_id = requestId;
  return p;
};

/**
 * The problem payload, or {error} when the form isn't ready. counted_qty only for count_off (and only a
 * whole number >= 0); the note trimmed (null when empty); the photo without its data: prefix.
 */
export function problemPayload({ kind, counted, note, photo, pin, requestId = null }) {
  if (!PROBLEM_KINDS.some((k) => k.kind === kind)) return { error: 'Pick what doesn’t match.' };
  const p = String(pin || '').trim();
  if (!p) return { error: 'Type your PIN.' };
  let countedQty = null;
  if (kind === 'count_off') {
    const raw = String(counted == null ? '' : counted).trim();
    if (raw !== '') {
      if (!/^\d+$/.test(raw)) return { error: 'The count must be a whole number.' };
      countedQty = parseInt(raw, 10);
    }
  }
  const n = String(note || '').trim();
  if (n.length > NOTE_MAX) return { error: `The note is too long (at most ${NOTE_MAX} characters).` };
  if (kind === 'other' && !n) return { error: 'Say what is wrong in the note.' };
  const photoB64 = stripDataPrefix(photo);
  if (photoB64 && base64Bytes(photoB64) > PHOTO_MAX_BYTES) return { error: 'That photo is too big. Take it again.' };
  return {
    payload: {
      pin: p,
      outcome: 'problem',
      problem_kind: kind,
      counted_qty: countedQty,
      note: n || null,
      photo_b64: photoB64 || null,
      ...(requestId ? { client_request_id: requestId } : {}),
    },
  };
}

/** A manager's Clear: {error} or null when it can go. */
export function clearProblemError({ pin, note }) {
  if (!String(pin || '').trim()) return 'A manager PIN is needed to clear it.';
  if (String(note || '').trim().length < 5) return 'Say what was done (at least 5 characters).';
  return null;
}

// ── The stack's layers (stackLayout.js, the twin of the API's stack_layout.py) ──────────────────────

/** The zone's layout: the API's own, else worked out from its units and tile grid. */
export function zoneLayout(zone) {
  if (!zone) return null;
  if (zone.layout && zone.layout.spots > 0) return zone.layout;
  return stackLayout(zone.units_planned, zone.tile_cols, zone.tile_rows, zone.stack_height);
}

/**
 * How the layers read: {grid, cols, rows, spots, maxHigh, fullLayers, topCount, topFull, cells}. Layers
 * 1..fullLayers fill every (non-empty) spot; the top layer (maxHigh) is the spots whose height reaches
 * it. cells[i] = {n: i + 1, height, top} in row-major order.
 */
export function stackLayers(zone) {
  const layout = zoneLayout(zone);
  if (!layout) return null;
  const spots = layout.spots;
  // A grid only when JobQ has one (tile_cols / tile_rows): a stack without one is `spots` loose columns,
  // never a grid made up here.
  const gc = Number(layout.tile_cols || zone.tile_cols) || 0;
  const gr = Number(layout.tile_rows || zone.tile_rows) || 0;
  const grid = gc > 0 && gr > 0;
  const cols = grid ? gc : spots;
  const rows = grid ? gr : 1;
  const maxHigh = layout.max_high;
  const fullLayers = layout.short_n > 0 ? layout.short_h : Math.max(0, maxHigh - 1);
  const cells = [];
  let topCount = 0;
  for (let i = 0; i < spots; i += 1) {
    const height = cellHeight(layout, i);
    const top = height === maxHigh && height > 0;
    if (top) topCount += 1;
    cells.push({ n: i + 1, height, top });
  }
  return { layout, grid, cols, rows, spots, maxHigh, fullLayers, topCount, topFull: topCount === spots, cells };
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** The first tab: "Layers 1–8", "Layer 1", or null when there is only the top layer. */
export function fullLayersTab(layers) {
  if (!layers || layers.fullLayers <= 0) return null;
  return layers.fullLayers === 1 ? 'Layer 1' : `Layers 1–${layers.fullLayers}`;
}

/** The second tab: "Layer 9 (top)". */
export const topLayerTab = (layers) => (layers ? `Layer ${layers.maxHigh} (top)` : 'Top layer');

/** The caption under the full layers. */
export function fullLayersCaption(layers) {
  if (!layers) return '';
  const n = layers.fullLayers;
  const per = layers.cells.filter((c) => c.height > 0).length;
  const range = n === 1 ? 'Layer 1' : `Layers 1 to ${n}`;
  return `${range}: a full ${layers.cols} × ${layers.rows} grid, ${plural(per, 'piece', 'pieces')} each. `
    + 'Pieces sit flat, edges lined up, no overhang.';
}

/** The caption under the top layer ("... The dashed spots stay empty."). */
export function topLayerCaption(layers) {
  if (!layers) return '';
  const x = layers.topCount;
  if (layers.topFull) {
    return `Top layer: ${plural(x, 'piece', 'pieces')}, a full ${layers.cols} × ${layers.rows} grid like the layers under it.`;
  }
  const fullRows = Math.floor(x / layers.cols);
  const rest = x - fullRows * layers.cols;
  let fill = '';
  if (fullRows > 0 && rest > 0) fill = ` Fill rows from the back: ${plural(fullRows, 'full row', 'full rows')} of ${layers.cols}, then ${rest}.`;
  else if (fullRows > 0) fill = ` Fill rows from the back: ${plural(fullRows, 'full row', 'full rows')} of ${layers.cols}.`;
  else fill = ` Fill the back row from the left: ${rest}.`;
  return `Top layer: ${plural(x, 'piece', 'pieces')}.${fill} The dashed spots stay empty.`;
}

/** A stack without a grid in words: "3 stacks: 10 high", "2 stacks: 10 high + 1 stack: 9 high". */
export function columnsPhrase(layers) {
  if (!layers) return '';
  const l = layers.layout;
  const part = (n, h) => `${plural(n, 'stack', 'stacks')}: ${h} high`;
  let t = part(l.tall_n, l.tall_h);
  if (l.short_n) t += ` + ${part(l.short_n, l.short_h)}`;
  return t;
}

/** Inches as the shop reads them, to the nearest 1/8: 1.08 -> "1 1/8", 0.5 -> "1/2", 3 -> "3". */
export function inchesText(value) {
  const v = Number(value);
  if (!Number.isFinite(v) || v <= 0) return null;
  const eighths = Math.round(v * 8);
  if (eighths === 0) return 'under 1/8';
  const whole = Math.floor(eighths / 8);
  let num = eighths % 8;
  let den = 8;
  while (num && num % 2 === 0) { num /= 2; den /= 2; }
  if (!num) return String(whole);
  return whole ? `${whole} ${num}/${den}` : `${num}/${den}`;
}

/** "12.5" / "507.6": a number without trailing zeros, at most `dp` places. */
export function num(value, dp = 1) {
  const v = Number(value);
  if (!Number.isFinite(v)) return '';
  return String(Math.round(v * 10 ** dp) / 10 ** dp);
}

/** "Nov 5" for an ISO date (read as the calendar day, no time zone shift). */
export function shortDate(iso) {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso));
  if (!m) return '';
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/** The big title under "You scanned": "Pallet 2 of 3 for SO-1144". */
export function palletHeadline(pallet, order) {
  if (!pallet) return '';
  const what = pallet.container_type === 'Box' ? 'Box' : 'Pallet';
  const of = pallet.pallets_total ? ` of ${pallet.pallets_total}` : '';
  const so = order && order.order_number ? ` for ${order.order_number}` : '';
  return `${what} ${pallet.pallet_num ?? ''}${of}${so}`.replace(/\s+/g, ' ').trim();
}

/** The line under it: "Faith Technologies · PO 5810056 · due Nov 5". */
export function orderLine(order) {
  if (!order) return '';
  return [order.customer_name, order.customer_po ? `PO ${order.customer_po}` : null,
    order.promised_date ? `due ${shortDate(order.promised_date)}` : null].filter(Boolean).join(' · ');
}

/** The chips under the identity block. */
export function identityChips(data) {
  if (!data) return [];
  const { pallet, beacon, counts, containers } = data;
  const chips = [];
  // a holding pallet (jobq-api's answer carries wip_task_id / wip_station): "Holding pallet · then Brake"
  if (pallet && isTaskWipPallet(pallet)) chips.push(holdingPalletText(pallet));
  if (beacon && beacon.beacon_id) chips.push(`Beacon ${beacon.beacon_id}`);
  const boxes = (containers || []).filter((c) => c.kind === 'box').length;
  if (boxes) chips.push(plural(boxes, 'box', 'boxes'));
  const kits = (data.kit_boxes || []).length;
  if (kits) chips.push(plural(kits, 'kit box', 'kit boxes'));
  if (pallet && pallet.container_type !== 'Box' && pallet.length_in && pallet.width_in) {
    chips.push(`${Math.round(pallet.length_in)} × ${Math.round(pallet.width_in)} pallet`);
  }
  if (counts) {
    chips.push(`${plural(counts.parts || 0, 'part', 'parts')} · ${counts.units_planned ?? 0} ${counts.unit || 'pcs'}`);
    if (counts.steel_weight_lbs != null) {
      chips.push(`${counts.weight_complete === false ? 'at least ' : ''}${num(counts.steel_weight_lbs, 0)} lb steel`);
    }
  }
  return chips;
}

/** A warning's lead (bold) and the rest: up to the first ": " or ". ". */
export function warningParts(message) {
  const s = String(message || '');
  const m = /^(.{1,80}?[.:])\s+(.*)$/s.exec(s);
  if (!m) return { lead: s, rest: '' };
  return { lead: m[1], rest: m[2] };
}

// ── "pallet" or "box" (data.noun: a Box container is a single carton) ──────────────────────────────

/** {noun, Noun} for the page's words. */
export function nounOf(data) {
  const noun = data && data.noun === 'box' ? 'box' : 'pallet';
  return { noun, Noun: noun === 'box' ? 'Box' : 'Pallet' };
}

/** A parts row's key: one row per (part, assembly), so mirror assemblies and spare legs stay apart. */
export const partRowKey = (p) => `${p.part_id}-${p.assembly_id == null ? 'part' : p.assembly_id}`;

/** A carton's bought items in all. */
export const purchasedCount = (c) => (c && Array.isArray(c.purchased_items) ? c.purchased_items : [])
  .reduce((s, i) => s + (Number(i.quantity) || 0), 0);
